/**
 * Plans Service
 * Handles eSIM plans/bundles API calls
 * Integrates with backend API
 */

import { apiClient } from '@/shared/services/api/client'
import type { ApiResponse } from '@/shared/types'
import { resolveCountryDisplayName } from '@/modules/countries/utils/countryIsoMap'

/**
 * Raw bundle response from API
 */
export interface ApiBundle {
  name: string
  description: string
  countries: Array<{
    name: string
    region: string
    iso: string
  }>
  dataAmount: number // in MB, -1 for unlimited
  duration: number // in days
  autostart: boolean
  unlimited: boolean
  roamingEnabled: boolean
  imageUrl?: string
  price: number
  group: string | null
  billingType: string
  potentialSpeeds: unknown
  /** Nomad API region; null for eSIM Go and Nomad single-country locals. */
  nomadRegion?: string | null
  /** eSIM Go global: roaming coverage when {@code countries} is only a Global marker row. */
  roamingCountries?: Array<{
    name: string
    region: string
    iso: string
  }>
}

/**
 * Transformed bundle for frontend use
 */
export interface Bundle {
  id: string // Uses bundle.name as ID
  name: string
  description: string
  price: number
  currency: string // Defaults to USD
  data: string // Formatted from dataAmount (e.g., "1GB", "Unlimited")
  validity: string // Formatted from duration (e.g., "7 days", "30 days")
  countries?: string[] // Country ISO codes (for backward compatibility)
  countryObjects?: Array<{ name: string; region: string; iso: string }> // Full country objects
  countryIso?: string // First country ISO
  countryName?: string // First country name
  duration: number // Days
  dataAmount: number // MB
  unlimited: boolean
  imageUrl?: string
  group?: string[] | null
  roamingEnabled?: boolean
  nomadRegion?: string | null
  [key: string]: unknown // For any additional fields
}

export interface BundlesResponse {
  bundles: ApiBundle[]
  /** Backend override for country bundle listing; see ListBundlesResponse.countrySellable (Java). */
  countrySellable?: boolean | null
}

/** Result of fetching bundles for a single country (includes optional sellability override). */
export interface CountryBundlesResult {
  bundles: Bundle[]
  countrySellable?: boolean
}

/**
 * Transform API bundle to frontend bundle format
 */
export function transformApiBundle(apiBundle: ApiBundle): Bundle {
  try {
    // Format data amount
    let dataDisplay: string
    if (apiBundle.unlimited || apiBundle.dataAmount === -1) {
      dataDisplay = 'Unlimited'
    } else if (apiBundle.dataAmount >= 1000) {
      // Convert MB to GB, keep 1 decimal place if needed
      const gb = apiBundle.dataAmount / 1000
      dataDisplay = gb % 1 === 0 ? `${gb}GB` : `${gb.toFixed(1)}GB`
    } else {
      dataDisplay = `${apiBundle.dataAmount}MB`
    }

    // Format validity
    const validityDisplay = `${apiBundle.duration} ${apiBundle.duration === 1 ? 'day' : 'days'}`

    const rawCountries = apiBundle.countries ?? []
    const roamingList = apiBundle.roamingCountries ?? []
    const c0 = rawCountries[0]
    const isGlobalMarker = [c0?.name, c0?.iso, c0?.region].some(
      v => String(v ?? '').toLowerCase() === 'global'
    )
    const countryObjectsForCoverage =
      isGlobalMarker && roamingList.length > 0 ? roamingList : rawCountries

    // Get country info (catalogue row — often "Global" for eSIM Go global SKUs)
    const firstCountry = rawCountries[0]
    const countryIso = firstCountry?.iso
    const countryName = resolveCountryDisplayName(firstCountry?.name ?? '', firstCountry?.iso)
    const countryIsos = countryObjectsForCoverage.map(c => c.iso).filter(Boolean) as string[]

    const catalogId = apiBundle.name
    const isNomad = catalogId.toLowerCase().startsWith('nomad:')
    const displayName = isNomad
      ? (apiBundle.description?.replace(/^Nomad - /i, '').trim() || catalogId)
      : catalogId

    return {
      id: catalogId, // eSIMGo: bundle name; Nomad: nomad:<productId>
      name: displayName,
      description: apiBundle.description || '',
      price: apiBundle.price || 0,
      currency: 'USD', // Default currency
      data: dataDisplay,
      validity: validityDisplay,
      countries: countryIsos, // ISO codes for backward compatibility
      countryObjects: countryObjectsForCoverage.map(c => ({
        ...c,
        name: resolveCountryDisplayName(c.name, c.iso),
      })),
      countryIso: countryIso,
      countryName: countryName,
      duration: apiBundle.duration || 0,
      dataAmount: apiBundle.dataAmount || 0,
      unlimited: apiBundle.unlimited || false,
      imageUrl: apiBundle.imageUrl,
      group: apiBundle.group ? (Array.isArray(apiBundle.group) ? apiBundle.group : [apiBundle.group]) : null,
      // Keep original fields for reference
      autostart: apiBundle.autostart,
      roamingEnabled: apiBundle.roamingEnabled,
      billingType: apiBundle.billingType,
      nomadRegion: apiBundle.nomadRegion ?? null,
    }
  } catch (error) {
    console.error('Error transforming bundle:', apiBundle, error)
    throw new Error(`Failed to transform bundle: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

class PlansService {
  /**
   * Get all bundles/plans
   * GET /api/v1/bundles
   * Requests all bundles by using a large size parameter
   * Requires JWT Bearer token authentication (automatically handled by API client)
   */
  async getBundles(): Promise<Bundle[]> {
    // Request all bundles by using a large size (backend returns all if size >= 1000)
    const response = await apiClient.get<ApiResponse<BundlesResponse>>('/bundles?size=10000')
    const apiBundles = response.data?.bundles || []
    return apiBundles.map(transformApiBundle)
  }

  /**
   * Get bundles by country
   * GET /api/v1/bundles?countryIso={code}
   * Requires JWT Bearer token authentication (automatically handled by API client)
   */
  async getBundlesByCountry(countryIso: string): Promise<CountryBundlesResult> {
    try {
      console.log('Fetching bundles for country ISO:', countryIso)
      const response = await apiClient.get<ApiResponse<BundlesResponse>>(
        `/bundles?countryIso=${countryIso}&size=1000`
      )
      console.log('Raw API response:', response)
      
      // Handle response structure
      const apiBundles = response?.data?.bundles || []
      const rawSellable = response?.data?.countrySellable
      const countrySellable =
        rawSellable === undefined || rawSellable === null ? undefined : Boolean(rawSellable)
      console.log('Extracted bundles array:', apiBundles)
      
      if (apiBundles.length === 0) {
        console.warn('No bundles found for country ISO:', countryIso)
      }
      
      const transformedBundles = apiBundles.map(transformApiBundle)
      console.log('Transformed bundles:', transformedBundles)
      return { bundles: transformedBundles, countrySellable }
    } catch (error) {
      console.error('Error fetching bundles by country:', error)
      throw error
    }
  }

  /**
   * Get bundle details by ID (name)
   * GET /api/v1/bundles/{bundleId}
   * Requires JWT Bearer token authentication (automatically handled by API client)
   */
  async getBundleById(bundleId: string): Promise<Bundle> {
    const encoded = encodeURIComponent(bundleId)
    const response = await apiClient.get<ApiResponse<ApiBundle>>(`/bundles/${encoded}`)
    if (!response.data) throw new Error('Bundle not found')
    return transformApiBundle(response.data)
  }

  /**
   * Get local eSIM bundles (single country)
   * GET /api/v1/bundles?type=local
   * Requests all bundles by using a large size parameter
   * Requires JWT Bearer token authentication (automatically handled by API client)
   */
  async getLocalBundles(): Promise<Bundle[]> {
    // Request all bundles by using a large size (backend returns all if size >= 1000)
    const response = await apiClient.get<ApiResponse<BundlesResponse>>('/bundles?type=local&size=10000')
    const apiBundles = response.data?.bundles || []
    return apiBundles.map(transformApiBundle)
  }

  /**
   * Get regional eSIM bundles (multiple countries in a region)
   * GET /api/v1/bundles?type=regional
   * Requests all bundles by using a large size parameter
   * Requires JWT Bearer token authentication (automatically handled by API client)
   */
  async getRegionalBundles(): Promise<Bundle[]> {
    // Request all bundles by using a large size (backend returns all if size >= 1000)
    const response = await apiClient.get<ApiResponse<BundlesResponse>>('/bundles?type=regional&size=10000')
    const apiBundles = response.data?.bundles || []
    return apiBundles.map(transformApiBundle)
  }

  /**
   * Nomad global eSIM bundles only ({@code region === "Global"}).
   * GET /api/v1/bundles?type=global
   */
  async getGlobalBundles(): Promise<Bundle[]> {
    // Request all bundles by using a large size (backend returns all if size >= 1000)
    const response = await apiClient.get<ApiResponse<BundlesResponse>>('/bundles?type=global&size=10000')
    const apiBundles = response.data?.bundles || []
    return apiBundles.map(transformApiBundle)
  }

  /**
   * Legacy methods for backward compatibility
   */
  async getPlans(): Promise<Bundle[]> {
    return this.getBundles()
  }

  async getPlansByCountry(countryCode: string): Promise<Bundle[]> {
    const r = await this.getBundlesByCountry(countryCode)
    return r.bundles
  }

  async getPlanById(planId: string): Promise<Bundle> {
    return this.getBundleById(planId)
  }
}

export const plansService = new PlansService()
